/**
 * Copyright (c) 2016-present GlossGenius. All rights reserved.
 */

import * as React from 'react';
import Link from 'next/link';

import cx from 'classnames';
import { CanvasAnimatedTile } from 'themes/canvas/components/CanvasAnimatedTile';

import { Heading } from 'components/Heading';
import CloseIcon from 'components/Icons/CloseV2.svg';

import { useAppContext } from 'hooks/context/useAppContext';
import { useNavigation } from 'hooks/navigation/useNavigation';

import { BagCounter } from 'routes/shop/bag/components/BagCounter/BagCounter';

import GGLogo from '../../../../../public/assets/logos/GG_Logo_Black.svg';
import MenuIcon from '../../../../../public/assets/themes/canvas/img/menu-icon.svg';
import PoweredBy from '../../../../../public/assets/themes/canvas/img/poweredby.svg';
// import { BillboardNavItem } from 'themes/canvas/components/Navigation/BillboardNavItem';
import { BillboardNavItem } from './BillboardNavItem';

import s from './BillboardNavigation.module.css';

const BillboardNavigation = () => {
  const {
    isMenuOpen,
    showTeam,
    showPortfolio,
    showShopLink,
    aboutUrl,
    teamUrl,
    servicesUrl,
    portfolioUrl,
    contactUrl,
    bookUrl,
    bagUrl,
    isPackagePurchaseEnabled,
    isFloating,
    packagesUrl,
    membershipsUrl,
    floatingNavRef,
    shopUrl,
    shopSubNavigationItems,
    renderOverlay,
    renderBagButton,
    renderMenuButton,
    tracking,
    renderMenuContainer,
    renderCloseButton,
    navigationRef,
    // socialMediaUrls,
  } = useNavigation({
    styles: {
      overlay: s.overlay,
      overlayOpen: s.overlayOpen,
      overlayClosed: s.overlayClosed,
      menuButton: cx(s.bagButton, s.button),
      menuContainer: s.menuContainer,
      menuContainerOpen: s.menuContainerOpen,
      menuContainerClosed: s.menuContainerClosed,
    },
  });

  const { publicUser } = useAppContext();

  return (
    <nav className={s.root} ref={navigationRef} aria-label="main navigation">
      <div className={s.topPlaceholder} ref={floatingNavRef} id="top-of-nav" />

      <div
        id="nav-bar"
        data-testid="nav-bar"
        className={cx(s.container, { [s.isFloating]: isFloating })}
      >
        <div className={s.title}>
          <Link href="/">{publicUser.business_name.toUpperCase()}</Link>
        </div>

        {renderOverlay()}
        <CanvasAnimatedTile className="navContainer">
          <div className={s.desktopFlexContainer}>
            <ul className={cx(s.desktopNav, s.menu)}>
              <BillboardNavItem to={servicesUrl}>Services</BillboardNavItem>
              <BillboardNavItem to={aboutUrl}>About</BillboardNavItem>
              {showTeam && (
                <BillboardNavItem to={teamUrl}>
                  <span data-testid="our-team">
                    <span className={s.fullText}>Team</span>
                    <span className={s.minimalText}>Team</span>
                  </span>
                </BillboardNavItem>
              )}
              {showPortfolio && (
                <BillboardNavItem to={portfolioUrl}>Portfolio</BillboardNavItem>
              )}
              {packagesUrl && !isPackagePurchaseEnabled && (
                <BillboardNavItem
                  onClick={() => tracking.navItemClicked('packages')}
                  to={packagesUrl}
                >
                  Packages
                </BillboardNavItem>
              )}
              {isPackagePurchaseEnabled && (
                <BillboardNavItem subNavItems={shopSubNavigationItems}>
                  Shop
                </BillboardNavItem>
              )}
              <BillboardNavItem to={contactUrl}>Contact</BillboardNavItem>
              {isPackagePurchaseEnabled && (
                <BillboardNavItem
                  to={bagUrl}
                  className={s.bagItem}
                  ariaLabel="Bag preview"
                >
                  <BagCounter size={28} />
                </BillboardNavItem>
              )}
              <BillboardNavItem className={s.bookNowCTA} to={bookUrl}>
                <div data-testid="book-now">
                  <span className={s.fullText}>Book Now</span>
                  <span className={s.minimalText}>Book</span>
                </div>
              </BillboardNavItem>
              {showShopLink && (
                <BillboardNavItem newTab to={shopUrl}>
                  <span className={s.fullText}>Shop Now</span>
                  <span className={s.minimalText}>Shop</span>
                </BillboardNavItem>
              )}

              {/* <SocialMediaLinks
          className={cx(s.navItem, s.iconLinks)}
          urls={socialMediaUrls}
        /> */}
            </ul>
          </div>
        </CanvasAnimatedTile>
        <div className={s.mobileMenuItems}>
          {renderBagButton(isMenuOpen ? s.hideOnOpenMobile : '')}
          {renderMenuButton({
            content: (
              <MenuIcon
                className={cx(s.menuIcon, isMenuOpen ? s.hideOnOpenMobile : '')}
              />
            ),
          })}
        </div>
        {renderMenuContainer({
          isOpen: isMenuOpen,
          content: (
            <>
              <div className={s.headerContent}>
                <Heading as="h2">
                  {publicUser?.business_name.toUpperCase()}
                </Heading>
                <div className={s.closeButtonContainer}>
                  {renderCloseButton({
                    content: <CloseIcon className={s.closeIcon} />,
                  })}
                </div>
              </div>
              {/* <div className={s.menuHeading}>
              <BillboardNavItemHome />
            </div> */}
              <ul className={cx(s.mobileNav, s.menu)}>
                <BillboardNavItem
                  onClick={() => tracking.navItemClicked('book')}
                  to={bookUrl}
                >
                  <div data-testid="book-now" className={s.bookNowCTA}>
                    <span className={s.fullText}>Book Now</span>
                    <span className={s.minimalText}>Book Now</span>
                  </div>
                </BillboardNavItem>
                <BillboardNavItem
                  onClick={() => tracking.navItemClicked('about')}
                  className={s.aboutPage}
                  to={aboutUrl}
                >
                  About
                </BillboardNavItem>
                {showTeam && (
                  <BillboardNavItem
                    onClick={() => tracking.navItemClicked('team')}
                    className={s.teamPage}
                    to={teamUrl}
                  >
                    <span data-testid="our-team">
                      <span className={s.fullText}>Our Team</span>
                      <span className={s.minimalText}>Team</span>
                    </span>
                  </BillboardNavItem>
                )}
                <BillboardNavItem
                  onClick={() => tracking.navItemClicked('services')}
                  className={cx(
                    !showTeam ? s.servicesWithNoTeam : s.servicesPage,
                  )}
                  to={servicesUrl}
                >
                  Services
                </BillboardNavItem>

                {showPortfolio && (
                  <BillboardNavItem
                    onClick={() => tracking.navItemClicked('portfolio')}
                    className={s.portfolioPage}
                    to={portfolioUrl}
                  >
                    Portfolio
                  </BillboardNavItem>
                )}
                {packagesUrl && (
                  <BillboardNavItem
                    onClick={() => tracking.navItemClicked('packages')}
                    to={packagesUrl}
                  >
                    Packages
                  </BillboardNavItem>
                )}
                {membershipsUrl && (
                  <BillboardNavItem
                    onClick={() => tracking.navItemClicked('memberships')}
                    to={membershipsUrl}
                  >
                    Memberships
                  </BillboardNavItem>
                )}
                <BillboardNavItem
                  onClick={() => tracking.navItemClicked('contact')}
                  className={cx(
                    showPortfolio
                      ? s.contactsPagePortfolioPersist
                      : s.contactsPage,
                  )}
                  to={contactUrl}
                >
                  Contact
                </BillboardNavItem>
                {showShopLink && (
                  <BillboardNavItem
                    onClick={() => tracking.navItemClicked('shop')}
                    className={s.shopNowCTA}
                    newTab
                    to={shopUrl}
                  >
                    <span className={s.fullText}>Shop Now</span>
                    <span className={s.minimalText}>Shop</span>
                  </BillboardNavItem>
                )}
              </ul>
              <div className={cx(s.copyright)}>
                <div>
                  <a
                    href="https://glossgenius.com"
                    className={s.logo}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <PoweredBy
                      className={s.poweredBy}
                      width="125"
                      height="13"
                    />
                    <GGLogo className={s.ggLogoSVG} width="125" height="13" />
                  </a>
                </div>
              </div>
            </>
          ),
        })}
      </div>
    </nav>
  );
};

export { BillboardNavigation };
